import { z } from 'zod'

/**
 * Groups (PRD-26): people this platform keeps together, and the unit every "who may" is
 * written in.
 *
 * A group is an object with an id, never a name somebody typed. Names are for people; ids are
 * for access, so renaming a group can never change who reaches what, and deleting one can never
 * leave a share quietly pointing at nobody.
 */

// ---------------------------------------------------------------------------
// Identity: a name, an icon, a colour (D-6)
// ---------------------------------------------------------------------------

/**
 * The curated icon set, in the order the chooser shows it (six rows of eight).
 *
 * Line icons from the set the product already draws with, checked to exist in the installed
 * `lucide-react`. Deliberately absent: every glyph the product already gives a meaning to —
 * `lock` (private), `globe` (everyone), `bot`, `book-open`, `brain`, `database`, folders,
 * shields, `id-card`, `zap`, `eye`, `flag`, `pin`, `bell`, `key-round`, `sparkles`,
 * `flask-conical`, `wrench`, `building-2`, `library`, status glyphs, and `users` (a role chip).
 * A group wearing one of those would say something it does not mean.
 */
export const GROUP_ICONS = [
  // Teams
  'users-round',
  'handshake',
  'heart-handshake',
  'hand-heart',
  'network',
  'puzzle',
  'trophy',
  'compass',
  // Business
  'briefcase-business',
  'landmark',
  'scale',
  'calculator',
  'banknote',
  'piggy-bank',
  'receipt',
  'chart-line',
  // Customers and communication
  'megaphone',
  'newspaper',
  'mail',
  'headset',
  'life-buoy',
  'store',
  'shopping-cart',
  'presentation',
  // Technology and science
  'code-xml',
  'server',
  'cloud',
  'cpu',
  'microscope',
  'atom',
  'dna',
  'rocket',
  // Operations and places
  'factory',
  'hard-hat',
  'truck',
  'package',
  'warehouse',
  'building',
  'map-pin',
  'plane',
  // Learning, health and culture
  'graduation-cap',
  'school',
  'stethoscope',
  'heart-pulse',
  'leaf',
  'sprout',
  'palette',
  'coffee'
] as const

export const GroupIconSchema = z.enum(GROUP_ICONS)
export type GroupIcon = z.infer<typeof GroupIconSchema>

export const DEFAULT_GROUP_ICON: GroupIcon = 'users-round'

/**
 * The six agent accent tokens, and only those: a group sits in the palette the product already
 * has (ADR-0012) and cannot paint outside it. Restated rather than imported from the marketplace
 * contract so this module has no dependencies; a unit test asserts the two lists are identical.
 */
export const GROUP_COLORS = ['slate', 'teal', 'plum', 'moss', 'navy', 'clay'] as const
export const GroupColorSchema = z.enum(GROUP_COLORS)
export type GroupColor = z.infer<typeof GroupColorSchema>

/** C0/C1 control characters and the bidirectional overrides that make a name read as another. */
// biome-ignore lint/suspicious/noControlCharactersInRegex: matching control characters is the point — a name holding one is refused.
const HIDDEN_CHARACTERS = /[\u0000-\u001f\u007f-\u009f\u200e\u200f\u202a-\u202e\u2066-\u2069]/

/**
 * A group's name: what people see wherever they share.
 *
 * Trimmed on the way in, 1–60 characters, and free of characters that are invisible or reorder
 * text — a name that renders as another group's name is an impersonation, not a typo.
 */
export const GroupNameSchema = z
  .string()
  .trim()
  .min(1, 'give the group a name')
  .max(60, 'keep the name to 60 characters')
  .refine((value) => !HIDDEN_CHARACTERS.test(value), 'the name contains a character that cannot be shown')

/**
 * A group as every other payload refers to one: enough to draw its avatar and say its name.
 *
 * `icon` and `color` fall back rather than fail, so an icon later retired from the set renders
 * the default instead of breaking whatever list the group appears in.
 */
export const GroupRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  icon: GroupIconSchema.catch(DEFAULT_GROUP_ICON),
  color: GroupColorSchema.catch('slate')
})
export type GroupRef = z.infer<typeof GroupRefSchema>

/** A group ref with its active-member count, for pickers, chips and reach lines. */
export const GroupWithSizeSchema = GroupRefSchema.extend({
  memberCount: z.number().int().nonnegative()
})
export type GroupWithSize = z.infer<typeof GroupWithSizeSchema>

/** A person as a group payload shows one: name and email only, never anything else (D-8). */
export const PersonRefSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  email: z.string(),
  avatarInitials: z.string()
})
export type PersonRef = z.infer<typeof PersonRefSchema>

/** A manager as everyone sees them (D-7): a name to ask. */
export const ManagerNameSchema = z.object({ id: z.uuid(), displayName: z.string() })
export type ManagerName = z.infer<typeof ManagerNameSchema>

// ---------------------------------------------------------------------------
// Reading groups
// ---------------------------------------------------------------------------

/** One group in a list or a picker (everyone may read these). */
export const GroupSummarySchema = GroupWithSizeSchema.extend({
  managers: z.array(ManagerNameSchema),
  isMember: z.boolean(),
  isManager: z.boolean()
})
export type GroupSummary = z.infer<typeof GroupSummarySchema>

export const GroupListSchema = z.object({
  items: z.array(GroupSummarySchema),
  /** How many groups matched in total; `items` stops at the requested limit. */
  total: z.number().int().nonnegative()
})
export type GroupList = z.infer<typeof GroupListSchema>

/** Who can use something, in one word, for a group page's lists. */
export const ReachKindSchema = z.enum(['private', 'groups', 'everyone', 'nobody'])
export type ReachKind = z.infer<typeof ReachKindSchema>

/** Something a group gives access to, or owns, as its page lists it. */
export const GroupAccessItemSchema = z.object({
  kind: z.enum(['knowledge_base', 'agent', 'connected_system', 'remote_agent']),
  id: z.string(),
  name: z.string(),
  /** For an agent: its identity tile. */
  icon: z.string().nullable(),
  accent: z.string().nullable(),
  reach: ReachKindSchema,
  /** How many groups it is shared with, when `reach` is `groups`. */
  groupCount: z.number().int().nonnegative(),
  /** Whether THIS reader can open it — a manager who is not a member usually cannot. */
  canOpen: z.boolean()
})
export type GroupAccessItem = z.infer<typeof GroupAccessItemSchema>

export const GroupRelationSchema = z.enum(['member', 'manager', 'member_and_manager', 'administrator', 'outsider'])
export type GroupRelation = z.infer<typeof GroupRelationSchema>

/** One group, as its page shows it. The insider-only parts are `null` for everyone else. */
export const GroupDetailSchema = GroupSummarySchema.extend({
  version: z.number().int().positive(),
  createdAt: z.iso.datetime(),
  relation: GroupRelationSchema,
  sharedWith: z.array(GroupAccessItemSchema).nullable(),
  ownedBy: z.array(GroupAccessItemSchema).nullable(),
  can: z.object({
    /** Add and remove members: its managers and administrators. */
    manageMembers: z.boolean(),
    /** Rename, restyle, appoint, delete: administrators. */
    administer: z.boolean()
  })
})
export type GroupDetail = z.infer<typeof GroupDetailSchema>

export const GroupMemberSchema = PersonRefSchema.extend({
  addedAt: z.iso.datetime(),
  addedByName: z.string().nullable(),
  isManager: z.boolean()
})
export type GroupMember = z.infer<typeof GroupMemberSchema>

export const GroupMembersPageSchema = z.object({
  items: z.array(GroupMemberSchema),
  nextCursor: z.string().nullable(),
  total: z.number().int().nonnegative()
})
export type GroupMembersPage = z.infer<typeof GroupMembersPageSchema>

// ---------------------------------------------------------------------------
// Writing groups
// ---------------------------------------------------------------------------

export const CreateGroupRequestSchema = z
  .object({
    name: GroupNameSchema,
    icon: GroupIconSchema.default(DEFAULT_GROUP_ICON),
    /** Omitted: the least used colour, so new groups do not all come out the same. */
    color: GroupColorSchema.optional(),
    managerIds: z.array(z.uuid()).max(20).default([])
  })
  .strict()
export type CreateGroupRequest = z.infer<typeof CreateGroupRequestSchema>

export const UpdateGroupRequestSchema = z
  .object({
    name: GroupNameSchema.optional(),
    icon: GroupIconSchema.optional(),
    color: GroupColorSchema.optional()
  })
  .strict()
export type UpdateGroupRequest = z.infer<typeof UpdateGroupRequestSchema>

export const AddMembersRequestSchema = z.object({ userIds: z.array(z.uuid()).min(1).max(200) }).strict()
export type AddMembersRequest = z.infer<typeof AddMembersRequestSchema>

export const MemberRefusalReasonSchema = z.enum(['not_found', 'deactivated', 'service'])

/** What an add did, per person: added, already there, or refused with the reason. */
export const AddMembersResultSchema = z.object({
  added: z.array(z.uuid()),
  already: z.array(z.uuid()),
  refused: z.array(z.object({ id: z.uuid(), reason: MemberRefusalReasonSchema }))
})
export type AddMembersResult = z.infer<typeof AddMembersResultSchema>

export const AppointManagerRequestSchema = z.object({ userId: z.uuid() }).strict()

// ---------------------------------------------------------------------------
// Owners (D-9, D-19, D-32)
// ---------------------------------------------------------------------------

/** Who something belongs to, as a request names it. */
export const OwnerRefSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('user'), id: z.uuid() }).strict(),
  z.object({ kind: z.literal('group'), id: z.uuid() }).strict()
])
export type OwnerRef = z.infer<typeof OwnerRefSchema>

/** Who something belongs to, as a payload shows it. */
export const OwnerViewSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('user'), id: z.uuid(), name: z.string() }),
  z.object({ kind: z.literal('group'), group: GroupRefSchema })
])
export type OwnerView = z.infer<typeof OwnerViewSchema>

// ---------------------------------------------------------------------------
// Deleting a group (D-26)
// ---------------------------------------------------------------------------

const NamedItemSchema = z.object({ id: z.string(), name: z.string() })

export const GroupDeletionPreviewSchema = z.object({
  group: GroupRefSchema,
  /** Binds the delete to exactly these consequences; a drifted digest is refused. */
  digest: z.string(),
  members: z.number().int().nonnegative(),
  managers: z.number().int().nonnegative(),
  sharedWith: z.object({
    knowledgeBases: z.array(NamedItemSchema.extend({ fallsBackToMaintainers: z.boolean() })),
    agents: z.array(NamedItemSchema.extend({ fallsBackToMaintainers: z.boolean() })),
    connectedSystems: z.array(NamedItemSchema.extend({ becomesUnusable: z.boolean() })),
    remoteAgents: z.array(NamedItemSchema.extend({ becomesUnusable: z.boolean() }))
  }),
  owns: z.object({
    knowledgeBases: z.array(NamedItemSchema),
    agents: z.array(NamedItemSchema.extend({ state: z.string() }))
  }),
  pendingRequests: z.object({ sharing: z.number().int().nonnegative(), lending: z.number().int().nonnegative() })
})
export type GroupDeletionPreview = z.infer<typeof GroupDeletionPreviewSchema>

export const DeleteGroupRequestSchema = z
  .object({
    confirmName: z.string().max(60).optional(),
    digest: z.string().min(1),
    /** Required when the group owns anything. */
    newOwner: OwnerRefSchema.optional()
  })
  .strict()
export type DeleteGroupRequest = z.infer<typeof DeleteGroupRequestSchema>

export const GroupDeletionResultSchema = z.object({
  deleted: z.literal(true),
  movedItems: z.number().int().nonnegative(),
  closedRequests: z.number().int().nonnegative()
})
export type GroupDeletionResult = z.infer<typeof GroupDeletionResultSchema>

// ---------------------------------------------------------------------------
// Finding people (D-8)
// ---------------------------------------------------------------------------

export const PeopleSearchResultSchema = z.object({ items: z.array(PersonRefSchema) })
export type PeopleSearchResult = z.infer<typeof PeopleSearchResultSchema>

export const ResolvePeopleRequestSchema = z
  .object({ emails: z.array(z.string().trim().min(1).max(320)).min(1).max(200) })
  .strict()

export const ResolvePeopleResultSchema = z.object({
  found: z.array(PersonRefSchema),
  /** Addresses with no account here — people appear only after they sign in once (D-5). */
  notFound: z.array(z.string())
})
export type ResolvePeopleResult = z.infer<typeof ResolvePeopleResultSchema>
